import { Layer, ObjectCategory, ZIndexes } from "@common/constants";
import { GRAVITY_PLASMA_BULLET } from "@common/definitions/gravity";
import { type GunDefinition, Guns } from "@common/definitions/items/guns";
import { BaseBullet, type BulletOptions } from "@common/utils/baseBullet";
import { RectangleHitbox } from "@common/utils/hitbox";
import { adjacentOrEqualLayer, adjacentOrEquivLayer, isUpperFloor } from "@common/utils/layer";
import { Geometry, Numeric, passesOverStair, stairLayerAt } from "@common/utils/math";
import { random, randomFloat, randomRotation } from "@common/utils/random";
import { Vec, type Vector } from "@common/utils/vector";
import { colord } from "colord";
import { BloomFilter, GlowFilter, MultiColorReplaceFilter } from "pixi-filters";
import { Color, Container, type Sprite } from "pixi.js";
import { GameConsole } from "../console/gameConsole";
import { Game } from "../game";
import { GravityFx } from "../gravity/gravityFx";
import { PlasmaFx, type PlasmaBolt } from "../gravity/plasmaFx";
import { CameraManager } from "../managers/cameraManager";
import { CombatFx, traitsOf } from "../managers/combatFx";
import { CombatSounds, GUN_CYCLE, GUNSHOT } from "../managers/combatSounds";
import { ParticleManager } from "../managers/particleManager";
import { SoundManager } from "../managers/soundManager";
import { PIXI_SCALE, SATURATION_SOUND_SPEED, SPLIT_SOUND_SPEED, THIN_SOUND_SPEED } from "../utils/constants";
import { TrenchioSprite, toPixiCoords } from "../utils/pixi";
import type { Building } from "./building";
import { BulletTargets } from "./bulletTargets";
import type { BulletStrike } from "./obstacle";
import { type Obstacle } from "./obstacle";
import { type Player } from "./player";
import type { Projectile } from "./projectile";

const white = 0xFFFFFF;

export class Bullet extends BaseBullet {
    private readonly _image: TrenchioSprite;
    readonly maxLength: number;
    readonly tracerLength: number;

    private _trailReachedMaxLength = false;
    private _trailTicks = 0;

    private _lastParticleTrail = Date.now();
    private _playBulletWhiz: boolean;

    /** the gun that fired it (none for shrapnel and the like) */
    private readonly _gun?: GunDefinition;
    /** a rifle's or sniper's tracer's bright head (managers/combatFx.ts) */
    private _head?: Sprite;

    /** a plasma burst's bolt: its glowing head, arcs and sparks (gravity/plasmaFx.ts); and what it struck */
    private _plasma?: PlasmaBolt;
    private _plasmaHit?: { readonly point: Vector, readonly normal: number, readonly player: boolean };

    constructor(options: BulletOptions) {
        super(options);

        const tracerStats = this.definition.tracer;

        this._image = new TrenchioSprite(tracerStats?.image ?? "base_trail")
            .setRotation(this.rotation - Math.PI / 2)
            .setZIndex(this.definition.tracer?.zIndex ?? ZIndexes.Bullets)
            .setVPos(toPixiCoords(this.position));

        const {
            length = 1,
            width = 1,
            opacity = 1
        } = tracerStats ?? {};
        const {
            length: lengthMod = 1,
            width: widthMod = 1,
            opacity: opacityMod = 1
        } = options.modifiers?.tracer ?? {};
        this.tracerLength = length * lengthMod;
        this.maxLength = this._image.width * this.tracerLength;
        this._image.scale.y = width * widthMod * (this.thin ? 0.5 : 1);
        this._image.alpha = opacity * opacityMod / (this.reflectionCount + 1);

        const coolerBulletTrailsEnabled = GameConsole.getBuiltInCVar("cv_cooler_graphics")
            && GameConsole.getBuiltInCVar("cv_bullet_trail_bloom");

        if (coolerBulletTrailsEnabled) {
            this._image.filters = [
                new BloomFilter({
                    strength: 5
                })
            ];
        }

        if (Game.mode.bulletFilters) {
            this._image.filters = [
                new GlowFilter({
                    distance: 15,
                    outerStrength: 2
                }),
                new MultiColorReplaceFilter({
                    replacements: [
                    [ [1,0,0], [0,0,1] ],
                    [ [0,1,0], [1,1,1] ]
                    ],
                    tolerance: 0.001
                })
            ];
        }

        this._image.anchor.set(1, 0.5);
        if (tracerStats?.spinSpeed !== undefined) {
            this._image.anchor.set(0.5, 0.5);
        }

        const color = new Color(
            tracerStats?.color === -1
                ? random(0, white)
                : tracerStats?.color ?? white
        );
        if (Game.mode.bulletTrailAdjust) color.multiply(Game.mode.bulletTrailAdjust);
        if (this.saturate) {
            const hsl = colord(color.toRgbaString()).saturate(50);
            color.value = (hsl.brightness() < 0.6 ? hsl.lighten(0.1) : hsl.darken(0.2)).rgba;
        }

        this._image.tint = color;

        this._gun = Guns.fromStringSafe(this.definition.idString.slice(0, -7));
        if (this.definition.idString !== GRAVITY_PLASMA_BULLET && this.reflectionCount === 0) {
            this._head = CombatFx.tracerHead(this._gun, color.toNumber());
            this._head?.position.copyFrom(this._image.position);
            if (this._head) this._head.zIndex = this._image.zIndex + 0.01;
        }

        // don't play bullet whiz if bullet originated within whiz hitbox
        this._playBulletWhiz = !Game.activePlayer?.bulletWhizHitbox.isPointInside(this.initialPosition);

        // the Gravity Gloves' plasma burst: the flash, sparks and sound at the hands (gravity/gravityFx.ts), and the
        // bolt's glowing head on its tail
        if (this.definition.idString === GRAVITY_PLASMA_BULLET) {
            const heading = Math.atan2(this.direction.y, this.direction.x);
            if (this.reflectionCount === 0) GravityFx.plasmaShot(this.initialPosition, heading, this.layer);
            this._plasma = PlasmaFx.bolt(this.position, this.layer, heading);
        }

        this.updateLayer();

        // Gun Sounds (shotFX basically makes it so that the sound is played once)
        if (this.shotFX) {
            const gunIdString = this.definition.idString.slice(0, -7);

            let soundSpeed: number;
            switch (true) {
                case this.split: {
                    soundSpeed = SPLIT_SOUND_SPEED;
                    break;
                }

                case this.saturate: {
                    soundSpeed = SATURATION_SOUND_SPEED;
                    break;
                }

                case this.thin: {
                    soundSpeed = THIN_SOUND_SPEED;
                    break;
                }

                default: {
                    soundSpeed = 1;
                }
            }

            const options = {
                position: this.position,
                layer: this.layer,
                speed: soundSpeed
            };

            // each shot a hair different, dulled by distance, echoing off the surroundings (managers/combatSounds.ts)
            SoundManager.play(`${gunIdString}_fire${this.lastShot ? "_last" : ""}`, { ...options, ...GUNSHOT });

            if (this.cycle) {
                SoundManager.play(`${gunIdString}_cycle`, { ...options, ...GUN_CYCLE });
            }
        }
    }

    /**
     * the tracer as a light, at night (1.05, managers/nightLight.ts): where its head is (units), the way it flies, how
     * long its trail is now (units) and how bright; nothing once its trail is gone
     */
    get glow(): { readonly x: number, readonly y: number, readonly angle: number, readonly length: number, readonly alpha: number } | undefined {
        const image = this._image;
        if (image.destroyed || !image.visible || image.width < 2 || image.alpha <= 0) return undefined;
        return { x: this.position.x, y: this.position.y, angle: Math.atan2(this.direction.y, this.direction.x), length: image.width / PIXI_SCALE, alpha: image.alpha };
    }

    update(delta: number): void {
        if (!this.dead) {
            // (0.98.8: what it could strike this frame, gathered once for every bullet, not every object the page knows)
            for (const collision of this.updateAndGetCollisions(delta, BulletTargets.near(this, delta))) {
                const object = collision.object;
                const { isProjectile, isObstacle, isPlayer } = object;

                if (object.isObstacle && object.definition.isStair) {
                    // (as the server's: over the opening of a flight up to a roof's deck, on the deck, on over it,
                    // 0.98.2)
                    if (passesOverStair(object.definition, object.layer, this.layer)) continue;
                    // (as the server's: off the side of the lower half of a flight with nothing beside it, the floor
                    // at its foot, 1.06.1: stairLayerAt)
                    const newLayer = stairLayerAt(
                        object.definition,
                        (object as Obstacle).orientation,
                        object.hitbox as RectangleHitbox,
                        object.layer,
                        this.position
                    );
                    this.layer = newLayer;
                    this.updateLayer();
                    continue;
                }

                // We check for projectiles first, not obstacles, otherwise stuff like tables or bushes won't be damaged by bullets.
                if (isProjectile && !adjacentOrEquivLayer(object, this.layer)) continue;

                const { point, normal } = collision.intersection;

                if ((isPlayer && collision.reflected) || (!isPlayer && this.reflective)) {
                    if (!CombatSounds.ricochet(collision.intersection.point, object.layer)) {
                        SoundManager.play(
                            `bullet_reflection_${random(1, 5)}`,
                            {
                                position: collision.intersection.point,
                                falloff: 0.2,
                                maxRange: 96,
                                layer: object.layer
                            });
                    }
                } else if (isPlayer) {
                    // the bullet's own line and weight decide the blood it throws (Blood & gore)
                    (object as Player).hitEffect(point, Math.atan2(normal.y, normal.x), undefined, { kind: "bullet", direction: this.direction, damage: this.definition.damage });
                } else if (isObstacle || object.isBuilding) {
                    // a bullet's own impact: its sound and what flies off (sparks, chips, splinters...); a bullet the
                    // surface turned (steel) glances off whining
                    // (it glances off only when the server bounces it too: at most 3 bounces, not a round that never
                    // bounces or bursts on impact, 0.85)
                    const def = this.definition;
                    const glanced = collision.reflected === true && this.reflectionCount < 3 && !def.noReflect
                        && (def.onHitExplosion === undefined || !def.explodeOnImpact);
                    const strike: BulletStrike = { direction: this.direction, damage: def.damage, glanced };
                    (object as Obstacle | Building).hitEffect(point, Math.atan2(normal.y, normal.x), strike);
                } else {
                    (object as Partial<Pick<Projectile, "hitEffect">>).hitEffect?.(point, Math.atan2(normal.y, normal.x));
                }

                this.collidedIDs.add(object.id);

                this.position = point;

                // We check here for obstacles, after the collision was done, in order to damage tables and bushes.
                // think of it as bullet penetration.
                if (isObstacle && object.definition.noCollisions) continue;

                if (this._plasma) this._plasmaHit = { point, normal: Math.atan2(normal.y, normal.x), player: isPlayer === true };
                this.dead = true;
                break;
            }

            if (this.definition.tracer?.spinSpeed !== undefined) {
                this._image.rotation += this.definition.tracer.spinSpeed;
            }
        }

        if (this._playBulletWhiz) {
            const intersection = Game.activePlayer?.bulletWhizHitbox.intersectsLine(this.initialPosition, this.position);
            if (intersection && adjacentOrEqualLayer(this.layer, Game.layer)) {
                // a rifle's bullet cracks past (it's faster than sound); slower ones whoosh by
                if (!CombatSounds.nearMiss(this._gun ? traitsOf(this._gun) : undefined, intersection.point, Game.layer)) {
                    SoundManager.play(`bullet_whiz_${random(1, 3)}`, { position: intersection.point });
                }
                this._playBulletWhiz = false;
            }
        }

        if (!this.dead && !this._trailReachedMaxLength) {
            this._trailTicks += delta;
        } else if (this.dead) {
            this._trailTicks -= delta;
        }

        const length = Numeric.min(
            Numeric.min(
                this.definition.speed * (this.modifiers?.speed ?? 1) * this._trailTicks,
                Geometry.distance(this.initialPosition, this.position)
            ) * PIXI_SCALE,
            this.maxLength
        );
        this._image.width = length;

        this._trailReachedMaxLength ||= length >= this.maxLength;

        this._image.setVPos(toPixiCoords(this.position));
        if (this._head) {
            // the head glows where the bullet is, while it flies (it's gone with the trail once it's struck)
            this._head.position.copyFrom(this._image.position);
            // (no mask: Pixi's mask is undefined then, never null. 0.84's first build compared it with null, and the heads
            // never showed: the audit)
            this._head.visible = !this.dead && !this._image.mask;
        }

        // the plasma bolt's head goes with it; where it ends, it bursts on what it struck, or fizzles out
        if (this._plasma) {
            if (!this.dead) this._plasma.follow(this.position, this.layer);
            else {
                const hit = this._plasmaHit;
                if (hit) PlasmaFx.impact(hit.point, hit.normal, this.layer, hit.player);
                else PlasmaFx.fizzle(this.position, Math.atan2(this.direction.y, this.direction.x), this.layer);
                this._plasma.destroy();
                this._plasma = undefined;
            }
        }

        if (
            (
                isUpperFloor(this.layer)
                && Game.layer === Layer.Ground
            )
            || (
                this.initialLayer === Layer.Basement
                && this.layer !== this.initialLayer
                && Game.layer === Layer.Ground
            )
        ) {
            let hasMask = false;
            for (const building of Game.objects.getCategory(ObjectCategory.Building)) {
                if (!building.maskHitbox?.isPointInside(this.position)) continue;

                if (building.mask) {
                    hasMask = true;
                    this._image.mask = building.mask;
                    break;
                }
            }
            if (!hasMask) this._image.mask = null;
        }

        const coolerBulletTrailsEnabled = GameConsole.getBuiltInCVar("cv_cooler_graphics")
            && GameConsole.getBuiltInCVar("cv_bullet_trail_bloom");

        if (
            this.definition.trail
            && (this.definition.ignoreCoolerGraphics || coolerBulletTrailsEnabled)
            && Date.now() - this._lastParticleTrail >= this.definition.trail.interval
        ) {
            const trail = this.definition.trail;
            ParticleManager.spawnParticles(
                trail.amount ?? 1,
                () => ({
                    frames: trail.frame,
                    speed: Vec.fromPolar(
                        randomRotation(),
                        randomFloat(trail.spreadSpeed.min, trail.spreadSpeed.max)
                    ),
                    position: this.position,
                    lifetime: random(trail.lifetime.min, trail.lifetime.max),
                    zIndex: ZIndexes.Bullets - 1,
                    scale: randomFloat(trail.scale.min, trail.scale.max),
                    alpha: {
                        start: randomFloat(trail.alpha.min, trail.alpha.max),
                        end: 0
                    },
                    layer: this.layer,
                    tint: trail.tint === -1
                        ? new Color({ h: random(0, 6) * 60, s: 60, l: 70 }).toNumber()
                        : trail.tint
                })
            );

            this._lastParticleTrail = Date.now();
        }

        if (this._trailTicks <= 0 && this.dead) {
            this.destroy();
        }
    }

    layerContainer?: Container;

    updateLayer(): void {
        this.layerContainer?.removeChild(this._image);
        if (this._head) this.layerContainer?.removeChild(this._head);
        (this.layerContainer = CameraManager.getContainer(this.layer)).addChild(this._image);
        if (this._head) this.layerContainer.addChild(this._head);
    }

    destroy(): void {
        this._image.destroy();
        this._head?.destroy();
        this._head = undefined;
        this._plasma?.destroy();
        this._plasma = undefined;
        Game.bullets.delete(this);
    }
}
